import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { Anchor, Button, Group, Modal, Select, Text } from "@mantine/core";

import { handleOnChange } from "@utils/handleOnChange";

import { getSeverityRatings } from "@data/dropdownValues";

import { useCreateAllergy } from "@hooks/mutations/patient/useCreateAllergy";

import { PatientAllergy } from "@customTypes/patient";

import { StyledAddAllergyModalContent } from "./AddAllergyModal.styled";


interface AddAllergyModalProps {
  opened: boolean;
  close: () => void;
}
export const AddAllergyModal = ({ opened, close }: AddAllergyModalProps) => {
  const { t } = useTranslation();

  const { patientId } = useParams();
  const [severity, setSeverity] = useState<string | null>(null);
  const [formData, setFormData] = useState({} as PatientAllergy);
  const { mutate: createAllergy, isLoading: isCreateAllergyLoading } =
    useCreateAllergy();

  const handleAddAllergy = () => {
    if (!formData.allergy || !severity) return;

    const newFormData = {
      ...formData,
      severity: formData.severity[0],
      date: new Date(),
      patientId,
    } as PatientAllergy;
    createAllergy(newFormData, {
      onSuccess: () => {
        setFormData({} as PatientAllergy);
        setSeverity(null);
        close();
      },
    });
    // console.log("Adding allergy");
  };

  useEffect(() => {
    if (severity) {
      setFormData({ ...formData, severity });
    }
  }, [severity]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-allergy")}</Text>}
      opened={opened}
      onClose={close}
      styles={{ body: { minHeight: "auto" } }}
      // scrollAreaComponent={ScrollAreaAutosize}
    >
      <StyledAddAllergyModalContent>
        <form className="form">
          <div className="form-field">
            <label htmlFor="name">
              {t("allergy-name")}
              <sup>*</sup>
            </label>
            <input
              type="text"
              id="name"
              placeholder={t("eg", { example: t("peanut") })}
              onChange={e => handleOnChange(e, setFormData, formData)}
              value={formData.allergy ?? ""}
              name="allergy"
            />
          </div>

          <div className="form-field">
            <Select
              label={t("severity")}
              withAsterisk
              placeholder={t("select-severity-rating")}
              data={getSeverityRatings()}
              value={severity}
              onChange={setSeverity}
            />
          </div>
        </form>

        <Text fz={14} mt="xs" c="dimmed">
          <Trans
            i18nKey={"severity-learn-more"}
            components={{
              anchor: (
                <Anchor
                  inherit
                  component={Link}
                  c="accent.4"
                  target="_blank"
                  rel="noopener noreferrer"
                  to="https://link.springer.com/article/10.1007/s40521-019-00229-8/tables/1"
                />
              ),
            }}
          />
        </Text>

        <Group mt={"xl"} justify="end">
          <PermissionGate permission="consultations.create"><Button
            onClick={handleAddAllergy}
            disabled={!formData.allergy || !severity}
            loading={isCreateAllergyLoading}>
            {t("add-allergy")}
          </Button></PermissionGate>
        </Group>
      </StyledAddAllergyModalContent>
    </Modal>
  );
};
